import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { isAmazonDistributionWarehouseBrand } from "@freightsimple/shared/Helpers/isAmazonDistributionWarehouseBrand";
import { ReferenceNumbersInput } from "@/Shared/ReferenceNumbers/Inputs/ReferenceNumbersInput";
import { useReferenceNumbersValidate } from "@/Shared/ReferenceNumbers/Hooks/useReferenceNumbersValidate";
import { AmazonReferenceIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonReferenceIdQuestionBubble";
import { AmazonShipmentIdQuestionBubble } from "@/Shared/ReferenceNumbers/Bubbles/AmazonShipmentIdQuestionBubble";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import BookShipmentSection from "../CustomComponents/BookShipmentSection";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";

interface ReferenceNumbersPanelProps {
  pickupReferenceNumber: string | undefined;
  deliveryReferenceNumber: string | undefined;
  deliveryAmazonReferenceId: string | undefined;
  deliveryAmazonShipmentId: string | undefined;
  onPickupReferenceNumberChanged: (newValue: string) => void;
  onDeliveryReferenceNumberChanged: (newValue: string) => void;
  onDeliveryAmazonReferenceIdChanged: (newValue: string) => void;
  onDeliveryAmazonShipmentIdChanged: (newValue: string) => void;
  deliveryLocation: AddLocationLocationType;
  index: number;
}
function BookShipmentReferenceNumbersPanel(props: ReferenceNumbersPanelProps) {
  // Mirrors the server-side gate exactly: locationType AND brand
  const isAmazonDelivery =
    props.deliveryLocation.locationType ===
      LocationType.DistributionWarehouse &&
    isAmazonDistributionWarehouseBrand(
      props.deliveryLocation.distributionWarehouseBrand,
    );

  const pickupValidation = useReferenceNumbersValidate({
    locationContext: LocationContext.Pickup,
    originalReferenceNumber: undefined,
  });

  const deliveryValidation = useReferenceNumbersValidate({
    locationContext: LocationContext.Delivery,
    originalReferenceNumber: undefined,
  });

  return (
    <BookShipmentSection
      id="shipment-references"
      index={props.index}
      header={
        <>
          Reference Numbers <Badge label="Optional" variant="neutral" />
        </>
      }
      description="Set reference numbers to help identify your shipments. You will have another opportunity to set these after getting quotes."
    >
      <Panel collapseOnNarrow>
        <Stack gap={4}>
          <ReferenceNumbersInput
            data-testid="pickup-reference-number-input"
            label="Pickup Reference Number"
            validation={pickupValidation}
            placeholder="Enter pickup reference number"
            value={props.pickupReferenceNumber}
            onChange={props.onPickupReferenceNumberChanged}
            maxLength={256}
          />
          {isAmazonDelivery ? (
            <>
              <FormItem
                label={
                  <>
                    Amazon Reference ID
                    <AmazonReferenceIdQuestionBubble
                      displayName="Delivery"
                      small
                    />
                  </>
                }
              >
                <TextInput
                  placeholder="Enter Amazon Reference ID"
                  width="full"
                  value={props.deliveryAmazonReferenceId ?? ""}
                  onChange={(e) =>
                    props.onDeliveryAmazonReferenceIdChanged(e.target.value)
                  }
                  maxLength={30}
                  data-testid="amazon-reference-id-input"
                />
              </FormItem>
              <FormItem
                label={
                  <>
                    Amazon Shipment ID
                    <AmazonShipmentIdQuestionBubble
                      displayName="Delivery"
                      small
                    />
                  </>
                }
              >
                <TextInput
                  placeholder="Enter Amazon Shipment ID"
                  width="full"
                  value={props.deliveryAmazonShipmentId ?? ""}
                  onChange={(e) =>
                    props.onDeliveryAmazonShipmentIdChanged(e.target.value)
                  }
                  maxLength={30}
                  data-testid="amazon-shipment-id-input"
                />
              </FormItem>
            </>
          ) : (
            <ReferenceNumbersInput
              data-testid="delivery-reference-number-input"
              label="Delivery Reference Number"
              validation={deliveryValidation}
              placeholder="Enter delivery reference number"
              value={props.deliveryReferenceNumber}
              onChange={props.onDeliveryReferenceNumberChanged}
              maxLength={256}
            />
          )}
        </Stack>
      </Panel>
    </BookShipmentSection>
  );
}
export default BookShipmentReferenceNumbersPanel;
